/**
 * Accountability Trail
 *
 * Shows who made which priority decisions and when, sourced directly from
 * governance_events for the current workspace. Filterable by event category,
 * actor, and date range.
 *
 * Route: /prioritization/settings/accountability
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Scale,
  Loader2,
  RefreshCw,
  User,
  Calendar,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  ArrowRight,
  GitMerge,
  Send,
  FileText,
  Clock,
  Layers,
  AlertCircle,
  Search,
} from 'lucide-react';
import { formatDistanceToNow, format, subDays, startOfDay } from 'date-fns';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import type { GovernanceEventType } from '../../../types/prioritization/governanceEvent';
import { formatGovernanceEventNote, formatGovernanceEventType } from '../../../lib/formatGovernanceEventDisplay';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';

// ── Types ─────────────────────────────────────────────────────────────────────

interface GovEvent {
  id: string;
  event_type: string;
  occurred_at: string;
  actor_id: string | null;
  actor_name: string | null;
  from_state: string | null;
  to_state: string | null;
  rationale: string | null;
  note: string | null;
  cycle_id: string | null;
  session_id: string | null;
  loop_id: string | null;
  workspace_id: string | null;
}

type DateRange = '7d' | '30d' | '90d' | 'all';

// ── Event display config ──────────────────────────────────────────────────────

const EVENT_CATEGORIES: Record<string, { label: string; types: GovernanceEventType[]; icon: typeof Scale; color: string; bg: string }> = {
  decisions: {
    label: 'Decisions',
    icon: CheckCircle2,
    color: 'text-[var(--green)]',
    bg: 'bg-[var(--green-dim)]',
    types: [
      'PROPOSAL_ACCEPTED', 'PROPOSAL_ACCEPTED_PROVISIONAL', 'LOOP_ACCEPTED',
      'PRIORITY_SET_DECIDED', 'PRIORITY_SET_RATIFIED', 'PRIORITY_SET_PUBLISHED',
      'FINAL_S6_DESIGNATED',
    ],
  },
  proposals: {
    label: 'Proposals',
    icon: Send,
    color: 'text-[var(--text-sec)]',
    bg: 'bg-[var(--slate-dim)]',
    types: ['PROPOSAL_SUBMITTED', 'PROPOSAL_SUPERSEDED', 'REQUEST_ISSUED', 'BALL_TOSSED_DOWN', 'BALL_TOSSED_UP'],
  },
  revisions: {
    label: 'Revisions & Rejections',
    icon: XCircle,
    color: 'text-[var(--coral)]',
    bg: 'bg-[var(--coral-dim)]',
    types: ['REVISION_REQUESTED', 'REVISION_CLOSED', 'LOOP_FINAL_REJECTED', 'LOOP_RETURNED'],
  },
  sessions: {
    label: 'Sessions',
    icon: Layers,
    color: 'text-[var(--text-sec)]',
    bg: 'bg-[var(--slate-dim)]',
    types: ['SESSION_CREATED', 'SESSION_STARTED', 'SESSION_COMPLETED', 'SESSION_PUBLISHED'],
  },
  cycles: {
    label: 'Cycles',
    icon: GitMerge,
    color: 'text-[var(--text-sec)]',
    bg: 'bg-[var(--slate-dim)]',
    types: ['CYCLE_CREATED', 'CYCLE_STARTED', 'CYCLE_PHASE_CHANGED', 'CYCLE_COMPLETED', 'CYCLE_CANCELLED'],
  },
  overrides: {
    label: 'Overrides',
    icon: AlertCircle,
    color: 'text-[var(--mustard)]',
    bg: 'bg-[var(--mustard-dim)]',
    types: ['OVERRIDE_APPLIED', 'RANK_OVERRIDE'],
  },
};

function getEventCategory(eventType: string) {
  for (const [, cat] of Object.entries(EVENT_CATEGORIES)) {
    if ((cat.types as string[]).includes(eventType)) return cat;
  }
  return {
    label: 'Other',
    icon: FileText,
    color: 'text-[var(--text-sec)]',
    bg: 'bg-[var(--hover-tint)]',
  };
}

function getEventLabel(eventType: string): string {
  return formatGovernanceEventType(eventType);
}

// ── Row component ─────────────────────────────────────────────────────────────

function EventRow({ event }: { event: GovEvent }) {
  const [expanded, setExpanded] = useState(false);
  const cat = getEventCategory(event.event_type);
  const Icon = cat.icon;
  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);
  const note = formatGovernanceEventNote(event.note, {
    eventType: event.event_type,
    resolveOrgUnitName: (id) => {
      const unit = orgUnits.get(id);
      return unit?.displayName || unit?.name;
    },
  });

  return (
    <div className="border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
      <button
        type="button"
        className="w-full flex items-start gap-3 px-4 py-3 hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors text-left"
        onClick={() => setExpanded(v => !v)}
      >
        {/* Icon */}
        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5 ${cat.bg}`}>
          <Icon className={`h-3.5 w-3.5 ${cat.color}`} />
        </div>

        {/* Main row */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {getEventLabel(event.event_type)}
            </span>
            {event.from_state && event.to_state && (
              <span className="flex items-center gap-1 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">
                <span className="font-mono bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-1.5 py-0.5 rounded">{event.from_state}</span>
                <ArrowRight className="h-3 w-3" />
                <span className="font-mono bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-1.5 py-0.5 rounded">{event.to_state}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {event.actor_name ?? event.actor_id?.slice(0, 8) ?? 'System'}
            </span>
            <span className="flex items-center gap-1" title={format(new Date(event.occurred_at), 'PPpp')}>
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(new Date(event.occurred_at), { addSuffix: true })}
            </span>
          </div>
          {event.rationale && !expanded && (
            <p className="mt-1 text-[12px] text-[var(--text-ter)] dark:text-[var(--text-dim)] truncate max-w-xl">
              {event.rationale}
            </p>
          )}
        </div>

        {/* Expand toggle */}
        {(event.rationale || note) ? (
          expanded
            ? <ChevronDown className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-1" />
            : <ChevronRight className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-1" />
        ) : null}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 pl-14 space-y-2">
          {event.rationale && (
            <div>
              <span className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider">Rationale</span>
              <p className="mt-1 text-[12px] text-[var(--text-sec)] dark:text-[var(--text-sec)]">{event.rationale}</p>
            </div>
          )}
          {note && (
            <div>
              <span className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider">Note</span>
              <p className="mt-1 text-[12px] text-[var(--text-sec)] dark:text-[var(--text-sec)]">{note}</p>
            </div>
          )}
          <div className="flex gap-4 mt-2 text-[11px] text-[var(--text-ter)]">
            <span>{format(new Date(event.occurred_at), 'PPpp')}</span>
            {event.cycle_id && <span>Cycle: <span className="font-mono">{event.cycle_id.slice(0, 8)}</span></span>}
            {event.session_id && <span>Session: <span className="font-mono">{event.session_id.slice(0, 8)}</span></span>}
            {event.loop_id && <span>Loop: <span className="font-mono">{event.loop_id.slice(0, 8)}</span></span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AccountabilityTrail() {
  const [events, setEvents] = useState<GovEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [actorSearch, setActorSearch] = useState('');

  const workspaceId = getCurrentWorkspaceId();

  const fetchEvents = useCallback(async () => {
    if (!workspaceId) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    setIsLoading(true);
    setError(null);

    let query = supabase
      .from('governance_events')
      .select('id, event_type, occurred_at, actor_id, actor_name, from_state, to_state, rationale, note, cycle_id, session_id, loop_id, workspace_id')
      .eq('workspace_id', workspaceId)
      .order('occurred_at', { ascending: false })
      .limit(500);

    if (dateRange !== 'all') {
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      query = query.gte('occurred_at', startOfDay(subDays(new Date(), days)).toISOString());
    }

    const { data, error: fetchError } = await query;

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setEvents((data ?? []) as GovEvent[]);
    }
    setIsLoading(false);
  }, [workspaceId, dateRange]);

  useEffect(() => { void fetchEvents(); }, [fetchEvents]);

  const filteredEvents = useMemo(() => {
    let result = events;

    if (selectedCategory !== 'all') {
      const cat = EVENT_CATEGORIES[selectedCategory];
      if (cat) result = result.filter(e => (cat.types as string[]).includes(e.event_type));
    }

    if (actorSearch.trim()) {
      const q = actorSearch.toLowerCase();
      result = result.filter(e =>
        e.actor_name?.toLowerCase().includes(q) ||
        e.actor_id?.toLowerCase().includes(q)
      );
    }

    return result;
  }, [events, selectedCategory, actorSearch]);

  // Unique actors for filter hint
  const actorCount = useMemo(() => {
    return new Set(events.map(e => e.actor_id ?? e.actor_name).filter(Boolean)).size;
  }, [events]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Scale className="h-4 w-4 text-[var(--mustard)]" />}
        iconBg="bg-[var(--mustard-dim)]"
        title="Accountability Trail"
        subtitle="Who made which priority decisions and when — append-only record from governance events"
        actions={
          <button
            onClick={() => void fetchEvents()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] border border-[var(--rule)] dark:border-[var(--rule-bright)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total events', value: events.length, icon: FileText },
          { label: 'Decision makers', value: actorCount, icon: User },
          { label: 'Shown', value: filteredEvents.length, icon: Scale },
        ].map(stat => (
          <div key={stat.label} className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-4 flex items-center gap-3">
            <stat.icon className="h-5 w-5 text-[var(--text-ter)] dark:text-[var(--text-dim)] flex-shrink-0" />
            <div>
              <div className="text-xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{stat.value}</div>
              <div className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-4 flex flex-wrap gap-3 items-center">
        {/* Category pills */}
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1 rounded-full text-[12px] font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-[var(--bg)] dark:bg-[var(--text-pri)] text-white'
                : 'bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]'
            }`}
          >
            All
          </button>
          {Object.entries(EVENT_CATEGORIES).map(([key, cat]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`px-3 py-1 rounded-full text-[12px] font-medium transition-colors ${
                selectedCategory === key
                  ? `${cat.bg} ${cat.color}`
                  : 'bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Actor search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)] pointer-events-none" />
          <input
            type="text"
            value={actorSearch}
            onChange={e => setActorSearch(e.target.value)}
            placeholder="Filter by person…"
            className="pl-8 pr-3 py-1.5 text-sm bg-[var(--surface)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule-bright)] focus:outline-none focus:ring-2 focus:ring-indigo-100 dark:focus:ring-[var(--active-tint)] focus:border-[var(--slate)] dark:text-[var(--text-pri)] w-44"
          />
        </div>

        {/* Date range */}
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          <select
            value={dateRange}
            onChange={e => setDateRange(e.target.value as DateRange)}
            className="text-sm bg-[var(--surface)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule-bright)] px-2 py-1.5 focus:outline-none dark:text-[var(--text-pri)]"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* Event timeline */}
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-[var(--text-ter)]">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading events…</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-3 px-6 py-8 text-[var(--coral)]">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[var(--text-ter)]">
            <Scale className="h-8 w-8 mb-3 opacity-30" />
            <p className="text-sm font-medium">No events found</p>
            <p className="text-[12px] mt-1">Try widening the date range or changing the category filter.</p>
          </div>
        ) : (
          <div className="divide-y-0">
            <div className="px-4 py-2.5 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)]/60 dark:bg-[var(--surface-raised)] flex items-center gap-2">
              <span className="text-[11px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wider">
                {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
              </span>
            </div>
            {filteredEvents.map(event => (
              <EventRow key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
